import {useMinSpin} from "../../hooks/useMinSpin";
import PageLoader from "../../shared/PageLoader/PageLoader";
import "./StatsPage.scss";
import {useEffect, useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../enum";
import {useI18n} from "../../i18n/I18nProvider";
import type {TranslationKey} from "../../i18n/ua";
import {ClockIcon, CrownIcon, DistanceIcon, HeroArt, RatioIcon, SkullIcon} from "./StatsArt";

interface ITopEntry {
    rank: number,
    name: string,
    value: number,
    kills: number,
    deaths: number,
    kdratio: number,
    longestKill: number,
    longestShot: number,
    playtime: number,
}

interface ITopStats {
    kills: ITopEntry[],
    kdratio: ITopEntry[],
    longest_kill: ITopEntry[],
    playtime: ITopEntry[],
}

interface IStatsServer {
    key: string,
    name: string,
}

type BoardKey = keyof ITopStats;

type TFn = (key: TranslationKey) => string;

const formatHours = (seconds: number, t: TFn) => {
    const hours = Math.floor(seconds / 3600);
    return hours > 0 ? `${hours} ${t("stats.hours")}` : `${Math.max(1, Math.floor(seconds / 60))} ${t("stats.minutes")}`;
};

const BOARDS: { key: BoardKey, titleKey: TranslationKey, labelKey: TranslationKey, icon: JSX.Element, format: (v: number, t: TFn) => string }[] = [
    { key: "kills", titleKey: "stats.kills", labelKey: "stats.killsLabel", icon: <SkullIcon />, format: (v) => v.toLocaleString("uk-UA") },
    { key: "kdratio", titleKey: "stats.kd", labelKey: "stats.kd", icon: <RatioIcon />, format: (v) => v.toFixed(2) },
    { key: "longest_kill", titleKey: "stats.distance", labelKey: "stats.distanceLabel", icon: <DistanceIcon />, format: (v, t) => `${Math.round(v)} ${t("stats.meters")}` },
    { key: "playtime", titleKey: "stats.playtime", labelKey: "stats.playtimeLabel", icon: <ClockIcon />, format: (v, t) => `${Math.floor(v / 3600).toLocaleString("uk-UA")} ${t("stats.hours")}` },
];

// додаткові колонки таблиці; колонку, що збігається з активною вкладкою, не дублюємо
const EXTRA_COLUMNS: { key: string, labelKey: TranslationKey, get: (e: ITopEntry, t: TFn) => string }[] = [
    { key: "kills", labelKey: "stats.kills", get: (e) => String(e.kills) },
    { key: "deaths", labelKey: "stats.deaths", get: (e) => String(e.deaths) },
    { key: "kdratio", labelKey: "stats.kd", get: (e) => e.kdratio.toFixed(2) },
    { key: "longest_kill", labelKey: "stats.farthest", get: (e, t) => `${Math.round(e.longestKill)} ${t("stats.meters")}` },
    { key: "playtime", labelKey: "stats.time", get: (e, t) => formatHours(e.playtime, t) },
];

const StatsPage = () => {
    const { t } = useI18n();
    const [stats, setStats] = useState<ITopStats | null>(null);
    const [error, setError] = useState(false);
    // лоадер видно щонайменше пів секунди (useMinSpin)
    const showLoader = useMinSpin(!error && !stats);
    const [tab, setTab] = useState<BoardKey>("kills");
    const [servers, setServers] = useState<IStatsServer[] | null>(null);
    // null — сервер за замовчуванням (перший зі списку), тож топ вантажиться паралельно зі списком серверів
    const [server, setServer] = useState<string | null>(null);

    useEffect(() => {
        axios.get(PATHS_API.STATS_SERVERS)
            .then(({ data }) => setServers(data))
            .catch((e) => {
                console.error("Помилка завантаження серверів:", e);
                setServers([]);
            });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setStats(null);
        setError(false);

        axios.get(PATHS_API.STATS_TOP, { params: server ? { server } : {} })
            .then(({ data }) => { if (!cancelled) setStats(data); })
            .catch((e) => {
                console.error("Помилка завантаження статистики:", e);
                if (!cancelled) setError(true);
            });

        return () => { cancelled = true; };
    }, [server]);

    const board = BOARDS.find((b) => b.key === tab)!;
    const entries = stats?.[tab] ?? [];
    const podium = entries.slice(0, 3);
    const rest = entries.slice(3);
    const extraColumns = EXTRA_COLUMNS.filter((c) => c.key !== tab);

    return (
        <div className="containerContent stats-page">
            <div className="stats-hero">
                <div>
                    <h3 className="stats-title">{t("stats.title")}</h3>
                    <p className="stats-subtitle">{t("stats.subtitle")}</p>
                </div>
                <HeroArt />
            </div>

            {/* місце під перемикач резервуємо одразу, щоб блоки нижче не стрибали, коли список серверів завантажиться */}
            { (servers === null || servers.length > 1) &&
                <div className="stats-servers-slot">
                    { servers &&
                        <div className="stats-servers">
                            { servers.map((s) =>
                                <button
                                    key={s.key}
                                    type="button"
                                    className={`stats-server ${(server ?? servers[0].key) === s.key ? "stats-server-active" : ""}`}
                                    onClick={() => setServer(s.key)}
                                >
                                    {s.name}
                                </button>
                            )}
                        </div>
                    }
                </div>
            }

            <div className="stats-tabs">
                { BOARDS.map((b) =>
                    <button
                        key={b.key}
                        type="button"
                        className={`stats-tab ${tab === b.key ? "stats-tab-active" : ""}`}
                        onClick={() => setTab(b.key)}
                    >
                        {b.icon}
                        {t(b.titleKey)}
                    </button>
                )}
            </div>

            { error && <div className="stats-error">{t("stats.error")}</div> }

            {/* поки дані вантажаться — спінер на весь екран */}
            { showLoader && <PageLoader size="lg" fullscreen label={t("common.loading")} /> }

            { !error && stats && entries.length === 0 &&
                <div className="stats-error">{t("stats.empty")}</div>
            }

            { !error && stats && entries.length > 0 &&
                <>
                    <div className="stats-podium stats-fade">
                        { podium.map((e) => e &&
                            <div key={e.rank} className={`stats-podium-card place-${e.rank}`}>
                                { e.rank === 1 && <CrownIcon className="stats-crown" /> }
                                <div className="stats-place">{e.rank}</div>
                                <div className="stats-podium-name" title={e.name}>{e.name}</div>
                                <div className="stats-podium-value">{board.format(e.value, t)}</div>
                                <div className="stats-podium-label">{t(board.labelKey)}</div>
                                <div className="stats-podium-details">
                                    <span>{t("stats.kd")} <b>{e.kdratio.toFixed(2)}</b></span>
                                    <span>{t("stats.deaths")} <b>{e.deaths}</b></span>
                                    <span>{t("stats.time")} <b>{formatHours(e.playtime, t)}</b></span>
                                </div>
                            </div>
                        )}
                    </div>

                    { rest.length > 0 &&
                        <div className="stats-table stats-fade">
                            <div className="stats-table-row stats-table-head">
                                <span>#</span>
                                <span>{t("stats.player")}</span>
                                <span className="col-main">{t(board.titleKey)}</span>
                                { extraColumns.map((c) => <span key={c.key} className="col-extra">{t(c.labelKey)}</span>) }
                            </div>
                            { rest.map((e) =>
                                <div key={e.rank} className="stats-table-row">
                                    <span className="stats-rank">{e.rank}</span>
                                    <span className="stats-name" title={e.name}>{e.name}</span>
                                    <span className="col-main stats-value">{board.format(e.value, t)}</span>
                                    { extraColumns.map((c) => <span key={c.key} className="col-extra">{c.get(e, t)}</span>) }
                                </div>
                            )}
                        </div>
                    }
                </>
            }
        </div>
    );
};

export default StatsPage;
